import React, { useEffect, useMemo, useState } from "react";
import "./Hero.css";
import { resolveStoreBadges } from "../../utils/storeLinks";
import heroDesktopAvif from "../../assets/hero-desktop.avif";
import heroDesktopWebp from "../../assets/hero-desktop.webp";
import heroMobileAvif from "../../assets/hero-mobile.avif";
import heroMobileWebp from "../../assets/hero-mobile.webp";

const STORE_BADGES = [
  {
    name: "Palm Store",
    href: null,
    img: "https://api.iconify.design/arcticons:palmstore.svg?color=%23000&height=46",
    alt: "Palm Store badge",
  },
  {
    name: "Samsung Galaxy Store",
    href: "https://apps.samsung.com/appquery/appDetail.as?appId=com.okirimobile.app&source=GBadge_01_8741821_tag&directOpen=true&ads=ddb0e6f9&nonOrgType=fce692ba",
    img: "https://commons.wikimedia.org/wiki/Special:FilePath/Samsung%20Galaxy%20Store%20logo.svg",
    alt: "Download Okiri on Samsung Galaxy Store",
  },
  {
    name: "App Store",
    href: "https://apps.apple.com/ng/app/okiri-mobile/id6760467395",
    img: "https://api.iconify.design/mdi:apple.svg?color=%23000&height=46",
    alt: "Download Okiri on the App Store",
  },
  {
    name: "APKPure",
    href: "https://apkpure.com/p/com.okirimobile.app",
    img: "https://static.apkpures.xyz/www/static/imgs/logo_new@2x.png",
    alt: "Download Okiri APK from APKPure",
  },
];

const WHO_FOR = [
  { label: "Therapists", icon: "🧠" },
  { label: "Relationship Coaches", icon: "💞" },
  { label: "Doctors", icon: "🩺" },
  { label: "Lawyers", icon: "⚖️" },
  { label: "Consultants", icon: "📊" },
  { label: "Creators", icon: "🎥" },
];

function useRotatingWords(words, opts) {
  const { intervalMs = 3000 } = opts || {};
  const safeWords = useMemo(() => (words || []).filter(Boolean), [words]);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (!safeWords.length) return;
    const t = setInterval(() => {
      setIndex((i) => (i + 1) % safeWords.length);
    }, intervalMs);
    return () => clearInterval(t);
  }, [safeWords, intervalMs]);

  return safeWords[index] || "";
}

function Hero() {
  const changingWord = useRotatingWords(
    ["Advice", "Support", "Guidance", "Consultations", "Insights", "Help"],
    { intervalMs: 3000 }
  );

  const storeBadges = resolveStoreBadges(STORE_BADGES);

  return (
    <>
      <section className="hero" id="top">
        <div className="hero__left">
          <h1>
            Stop Taking <span className="highlight">Free Calls.</span>
          </h1>

          <p>Nigeria’s first voice monetization platform for professionals.</p>

          <div className="hero__ctaRow">
            <span className="hero__ctaHint">Set your rate • Get paid per minute</span>
          </div>

          <div className="hero__value">
            <div className="hero__valueItem">
              <h3>You control your rate</h3>
              <p>Set your price per minute and decide who you want to talk to.</p>
            </div>

            <div className="hero__valueItem">
              <h3>Get paid for your time</h3>
              <p>Perfect for consultations, coaching, advice, and paid calls.</p>
            </div>

            <div className="hero__valueItem">
              <h3>Trust and safety first</h3>
              <p>KYC helps protect users and reduce fraud across the platform.</p>
            </div>
          </div>

          <div className="hero__images">
            {storeBadges.map((s) => {
              const badgeVisual = (
                <span className="badgeBox">
                  <img
                    className="storeBadgeImg"
                    src={s.img}
                    alt={s.alt}
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.style.display = "none";
                    }}
                  />
                </span>
              );

              const item = (
                <span className="badgeItemInner">
                  {badgeVisual}
                  <span className="badgeLabel">
                    {s.name}
                    {s.comingSoon ? <span className="badgeSoon"> • Soon</span> : null}
                    {s.recommended ? (
                      <span
                        style={{
                          display: "block", width: "fit-content", marginTop: 4, padding: "1px 6px",
                          borderRadius: 999, fontSize: "0.7em", fontWeight: 700,
                          background: "#7c5cff", color: "#fff", whiteSpace: "nowrap",
                        }}
                      >
                        ★ For your device
                      </span>
                    ) : null}
                  </span>
                </span>
              );

              if (s.href) {
                return (
                  <a
                    key={s.name}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="badgeLink"
                    aria-label={s.recommended ? `${s.name} — recommended for your device` : s.name}
                    title={s.name}
                  >
                    {item}
                  </a>
                );
              }

              if (s.comingSoon) {
                return (
                  <span
                    key={s.name}
                    className="badgeLink comingSoon"
                    aria-label={s.name}
                    title="Coming to this store soon"
                  >
                    {item}
                  </span>
                );
              }

              return (
                <span
                  key={s.name}
                  className="badgeLink disabled"
                  aria-label={s.name}
                  title={s.name}
                >
                  {item}
                </span>
              );
            })}
          </div>
        </div>

        <div className="hero__right">
          <picture>
            <source
              srcSet={heroMobileAvif}
              type="image/avif"
              media="(max-width: 767px)"
            />
            <source
              srcSet={heroMobileWebp}
              type="image/webp"
              media="(max-width: 767px)"
            />
            <source
              srcSet={heroDesktopAvif}
              type="image/avif"
              media="(min-width: 768px)"
            />
            <source
              srcSet={heroDesktopWebp}
              type="image/webp"
              media="(min-width: 768px)"
            />
            <img
              className="heroMainImage"
              src={heroMobileWebp}
              srcSet={`${heroMobileWebp} 768w, ${heroDesktopWebp} 1179w`}
              alt="Okiri app interface showing paid voice calls, wallet balance, and call scheduling"
              width="1179"
              height="1275"
              sizes="(max-width: 767px) 92vw, (max-width: 1200px) 48vw, 560px"
              fetchPriority="high"
              loading="eager"
              decoding="async"
            />
          </picture>
        </div>
      </section>

      <section className="whoFor">
        <div className="whoFor__inner">
          <div className="whoFor__header">
            <h2 className="whoFor__title">
              If People Call You For{" "}
              <span className="whoFor__typeSlot" aria-live="polite">
                <span key={changingWord} className="whoFor__typeWord highlight">
                  {changingWord}
                </span>
              </span>
              ~ This Is For You
            </h2>

            <p>
              Stop giving away your knowledge for free. Okiri helps you charge fairly and
              get paid securely.
            </p>
          </div>

          <div className="whoFor__grid">
            {WHO_FOR.map((x) => (
              <div key={x.label} className="whoFor__card">
                <div className="whoFor__icon" aria-hidden="true">
                  {x.icon}
                </div>
                <div className="whoFor__label">{x.label}</div>
                <div className="whoFor__sub">Paid calls • Per-minute pricing</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

export default Hero;
